import React from "react";
import MetricInfoIcon from "./ui/MetricInfoIcon";
import type { MetricSample } from "../hooks/useMetricSamples";
import { formatCompact } from "@/utils/formatters";

// =============================================================================
// STAT CARD
// =============================================================================
// PURPOSE: Enhanced KPI display card with 4 variants, percent change indicators, and interactive metric tooltips.
//
// REQUIREMENTS:
// - 4 variants: colored (green/orange/blue/darkblue/gray), icon-based, context-style (left border), compact
// - Optional percent change badge (↑ green, ↓ red)
// - Interactive info icon (ⓘ) triggers portal-based MetricTooltip with formula + samples
// - Responsive sizing (compact mode for dense layouts)
//
// KEY DECISIONS:
// - Color variants have fixed-height title area (reserves space for 2 lines) to prevent layout shift
// - Icon variant uses circular blue background with padding
// - Context variant uses left border indicator (blue default, red for alerts)
// =============================================================================

type CardColor = "green" | "orange" | "blue" | "darkblue" | "gray" | "none" | "glass";

interface StatCardProps {
  title: string;
  value: string | number;
  icon?: React.ReactNode;
  subValue?: string;
  subtext?: string;
  alert?: boolean;
  className?: string;
  color?: CardColor;
  percentChange?: number;
  /** How to format the change badge. Default "percent". */
  changeFormat?: "percent" | "dollar" | "number";
  compact?: boolean;
  metricKey?: string;
  getSamples?: (key: string) => MetricSample[];
}

const colorMap: Record<CardColor, string> = {
  green: "bg-emerald-600 text-glass border-emerald-700",
  orange: "bg-orange-500 text-glass border-orange-600",
  blue: "bg-blue-600 text-glass border-blue-700",
  darkblue: "bg-[#1e293b] text-glass border-slate-700",
  gray: "bg-slate-700 text-glass border-slate-600",
  none: "bg-black/[0.05] text-glass border-black/[0.08]",
  glass: "", // handled separately in render
};

const PercentBadge: React.FC<{ value: number; format?: "percent" | "dollar" | "number" }> = ({
  value,
  format = "percent",
}) => {
  if (value === 0) return null;
  const isPositive = value > 0;
  const abs = Math.abs(value);
  let display: string;
  if (format === "dollar") {
    display = formatCompact(abs);
  } else if (format === "number") {
    display = abs.toString();
  } else {
    display = `${abs.toFixed(1)}%`;
  }
  return (
    <span
      className={`inline-flex items-center text-xs font-medium ${isPositive ? "text-ds-green" : "text-ds-red"}`}
    >
      <span className="mr-0.5">{isPositive ? "\u25B2" : "\u25BC"}</span>
      {display}
    </span>
  );
};

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  subValue,
  subtext,
  alert,
  className = "",
  color,
  percentChange,
  changeFormat,
  compact,
  metricKey,
  getSamples,
}) => {
  // Glass variant - for use on background images
  if (color === "glass") {
    const titleEl = (
      <p
        className={`font-medium uppercase tracking-wide text-glass leading-tight drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)] ${compact ? "token-10" : "text-xs"}`}
      >
        {title}
      </p>
    );

    return (
      <div
        className={`liquid-glass anim-lift ${compact ? "p-3" : "p-4"} flex flex-col ${alert ? "border-l-4 border-red-500" : ""} ${className}`}
      >
        {/* Fixed height title area - always reserves space for 2 lines */}
        <div className={`${compact ? "h-[32px]" : "h-[40px]"} flex items-start`}>
          {metricKey && getSamples ? (
            <MetricInfoIcon metricKey={metricKey} getSamples={getSamples}>
              {titleEl}
            </MetricInfoIcon>
          ) : (
            titleEl
          )}
        </div>
        <p
          className={`font-bold text-glass drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)] ${alert ? "text-red-400" : ""} ${compact ? "text-xl" : "text-3xl"}`}
        >
          {value}
        </p>
        {(percentChange !== undefined || subtext || subValue) && (
          <div className="mt-1">
            {percentChange !== undefined && (
              <PercentBadge value={percentChange} format={changeFormat} />
            )}
            {subValue && (
              <p className="text-sm text-glass-muted drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
                {subValue}
              </p>
            )}
            {subtext && (
              <p className="text-xs text-glass-muted drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
                {subtext}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }

  // Colored variant (new dashboard style)
  if (color && color !== "none") {
    const titleEl = (
      <p
        className={`font-medium uppercase tracking-wide opacity-80 leading-tight ${compact ? "token-10" : "text-xs"}`}
      >
        {title}
      </p>
    );

    return (
      <div
        className={`${colorMap[color]} rounded-2xl border ${compact ? "p-3" : "p-4"} flex flex-col ${className}`}
      >
        {/* Fixed height title area - always reserves space for 2 lines */}
        <div className={`${compact ? "h-[32px]" : "h-[40px]"} flex items-start`}>
          {metricKey && getSamples ? (
            <MetricInfoIcon metricKey={metricKey} getSamples={getSamples}>
              {titleEl}
            </MetricInfoIcon>
          ) : (
            titleEl
          )}
        </div>
        <p className={`font-bold ${compact ? "text-xl" : "text-2xl"}`}>{value}</p>
        {(percentChange !== undefined || subtext) && (
          <div className="mt-1">
            {percentChange !== undefined && (
              <PercentBadge value={percentChange} format={changeFormat} />
            )}
            {subtext && <p className="text-xs opacity-70">{subtext}</p>}
          </div>
        )}
      </div>
    );
  }

  // Classic style with icon
  if (icon) {
    const titleEl = (
      <p className="text-sm font-medium text-glass-muted uppercase tracking-wide">{title}</p>
    );

    return (
      <div
        className={`bg-black/[0.05] p-6 rounded-2xl shadow-sm border border-black/[0.08] flex items-center space-x-4 hover:shadow-md transition-shadow ${className}`}
      >
        <div className="bg-blue-500/10 text-blue-400 p-3 rounded-full border border-blue-500/15">
          {icon}
        </div>
        <div>
          {metricKey && getSamples ? (
            <MetricInfoIcon metricKey={metricKey} getSamples={getSamples}>
              {titleEl}
            </MetricInfoIcon>
          ) : (
            titleEl
          )}
          <p className="text-2xl font-bold text-glass">{value}</p>
          {percentChange !== undefined && (
            <PercentBadge value={percentChange} format={changeFormat} />
          )}
        </div>
      </div>
    );
  }

  // Context style (no icon, border indicators)
  const titleEl = (
    <p
      className={`text-glass-muted uppercase font-bold tracking-wider ${compact ? "token-10" : "text-xs"}`}
    >
      {title}
    </p>
  );

  return (
    <div
      className={`bg-black/[0.05] ${compact ? "p-3" : "p-6"} rounded-2xl shadow-sm border-l-4 ${alert ? "border-red-500" : "border-blue-500"} border-t border-r border-b border-black/[0.08] ${className}`}
    >
      {metricKey && getSamples ? (
        <MetricInfoIcon metricKey={metricKey} getSamples={getSamples}>
          {titleEl}
        </MetricInfoIcon>
      ) : (
        titleEl
      )}
      <p
        className={`font-bold mt-1 ${alert ? "text-red-400" : "text-glass"} ${compact ? "text-xl" : "text-3xl"}`}
      >
        {value}
      </p>
      {percentChange !== undefined && <PercentBadge value={percentChange} format={changeFormat} />}
      {subValue && <p className="text-sm text-glass-muted mt-1">{subValue}</p>}
      {subtext && <p className="text-xs text-glass-muted mt-2 italic">{subtext}</p>}
    </div>
  );
};

export default StatCard;
